import React, { useEffect, useMemo, useState } from 'react';

import { Column, useStyles, TextSwitchColor, TextGreyColor } from './RepresentationAndRole.styles';
import { Box, Button, Grid, InputLabel, MenuItem } from '@material-ui/core';
import { Backdrop, Select, SelectArrowDownIcon, Typography } from '@room-match/shared-ui-components';
import { IProject } from 'shared/interfaces/IProject';
import { FormikProps } from 'formik';
import { IAuditionScheduleCreatePayload } from 'shared/interfaces/IAuditionSchedule';
import { profileService } from 'shared/services/profileService';
import { IProfile } from 'shared/interfaces/IProfile';
import { COLORS } from 'shared/constants/COLORS';
import ProjectOverview from 'pages/Jobs/ProjectOverview/ProjectOverview';

type Props = {
  projectData?: IProject;
  form: FormikProps<IAuditionScheduleCreatePayload>;
  submitted: boolean;
  noReq: boolean;
  confirm: VoidFunction;
  createIsLoading: boolean;
  disableRole?: boolean;
};

const { getProfiles } = profileService();

const RepresentationAndRole: React.FC<Props> = ({
  disableRole,
  form,
  projectData,
  submitted,
  confirm,
  createIsLoading,
  noReq,
}) => {
  const classes = useStyles();

  const { data: profileList, isLoading: profileLoading } = getProfiles();
  const [hidden, sethidden] = useState(false);
  const role = () =>
    projectData?.attributes?.self_tapes_roles?.data
      .filter((f) => f.attributes.self_tapes_requirements)
      .map((r) => {
        return { value: r.id, key: r.attributes.name };
      }) || [{ key: '', value: '' }];

  const profile = () =>
    profileList?.data
      // .filter((f) => !!f.attributes.agency_name)
      .map((i: IProfile) => {
        const hasDivisions = i.attributes.division?.data;
        const key = hasDivisions
          ? `${i.attributes?.agency_full_name || i.attributes.agency_name} - ${
              i.attributes.division.data.attributes.name
            }`
          : i.attributes?.agency_full_name || i.attributes.agency_name || 'Non-Represented';
        return { value: i.id, key };
      }) || [{ key: '', value: '' }];

  useEffect(() => {
    if (projectData) {
      const roles = projectData.attributes.self_tapes_roles.data.filter((f) => f.attributes.self_tapes_requirements);
      if (roles.length === 1) {
        form.setFieldValue('role_id', roles[0].id);
      }
    }
  }, [projectData]);

  useEffect(() => {
    if (profileList) {
      const profile = profileList.data;
      if (profile.length === 1) {
        form.setFieldValue('profile_id', profile[0].id);
      }
    }
  }, [profileList]);

  useEffect(() => {
    if (projectData && profileList) {
      const roles = projectData.attributes.self_tapes_roles.data;
      const profile = profileList.data;
      if (profile.length === 1 && roles.length === 1) {
        if (form.values.profile_id && form.values.role_id && !hidden) {
          confirm();
        }
      }
    }
  }, [profileList, projectData, form]);

  ///memo for selected role info based on role_id
  const selectedRole = useMemo(
    () => projectData?.attributes?.self_tapes_roles.data.find((f) => f.id === form.values.role_id),
    [form.values.role_id, projectData],
  );

  if (hidden) return <></>;
  if (profileLoading) return <Backdrop isLoading={true} />;

  return (
    <div className={classes.wrapper}>
      <Grid className={classes.root}>
        <Grid item className={classes.heading}>
          <TextSwitchColor size={18}>{projectData?.attributes?.company_name}</TextSwitchColor>
        </Grid>
        <Column>
          <Box>
            <InputLabel id="representationStatus">Representation</InputLabel>
            <Select
              disabled={profileList && profileList?.data.length === 1}
              variant="outlined"
              fullWidth
              error={!form.values.profile_id}
              data={profile()}
              value={form.values.profile_id}
              // IconComponent={SelectArrowDownIcon}
              className={classes.select}
              renderValue={(value) => {
                const item = profileList?.data.find((f: IProfile) => f.id === value);
                const agency = item?.attributes?.agency_full_name || item?.attributes?.agency_name || 'Non-Represented';
                const division = item?.attributes.division?.data && item?.attributes.division?.data.attributes.name;
                return form.values.profile_id ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextSwitchColor weight={500}>{agency}</TextSwitchColor>
                    {division ? (
                      <TextGreyColor weight={700} size={12}>{`${division}`}</TextGreyColor>
                    ) : (
                      <React.Fragment />
                    )}
                  </div>
                ) : (
                  <TextSwitchColor fontSize={14}>Select Representation</TextSwitchColor>
                );
              }}
              name="profile_id"
              renderMenu={(item) => {
                const agency = item.key.split(' - ')[0];
                const division = item.key.split(' - ')[1];
                return (
                  <MenuItem
                    style={{ background: item.value === form.values.profile_id ? COLORS.PRIMARY : 'unset' }}
                    onClick={() => form.setFieldValue('profile_id', item.value)}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        fontWeight={500}
                        style={{ color: item.value === form.values.profile_id ? 'white' : 'unset' }}
                      >
                        {agency}
                      </Typography>
                      {division ? (
                        <Typography
                          style={{ color: item.value === form.values.profile_id ? 'white' : '#98A1AC' }}
                          fontWeight={700}
                          fontSize={12}
                          color="darkGrey"
                        >{`${division}`}</Typography>
                      ) : (
                        <React.Fragment />
                      )}
                    </div>
                  </MenuItem>
                );
              }}
            />
          </Box>

          <Box>
            <InputLabel id="representationStatus">Role</InputLabel>
            <Select
              disabled={
                disableRole ||
                (projectData &&
                  projectData.attributes.self_tapes_roles.data.filter((f) => f.attributes.self_tapes_requirements)
                    .length === 1)
              }
              variant="outlined"
              fullWidth
              data={role()}
              value={form.values.role_id}
              IconComponent={SelectArrowDownIcon}
              name="role_id"
              onChange={form.handleChange}
            />
          </Box>

          <Grid style={{ marginTop: 'auto' }}>
            {submitted && (
              <Typography style={{ color: COLORS.DANGER, marginBottom: 5 }}>Self-tape already submitted</Typography>
            )}
            {noReq && (
              <Typography style={{ color: COLORS.DANGER, marginTop: 5 }}>Self-tape has no requirements</Typography>
            )}
            <Button
              disabled={form.values.profile_id === '' || form.values.role_id === '' || createIsLoading}
              variant="contained"
              color="primary"
              fullWidth
              className={classes.buttonCls}
              onClick={() => form.handleSubmit()}
            >
              {createIsLoading ? 'Saving...' : 'Confirm'}
            </Button>
          </Grid>
        </Column>
      </Grid>
      {projectData ? (
        <div style={{ flex: 1 }}>
          <ProjectOverview
            rolesData={
              projectData?.attributes.self_tapes_roles.data
                .filter((f) => (form.values.role_id ? form.values.role_id === f.id : true))
                .map((r) => r.attributes) || []
            }
            projectId={projectData?.id || ''}
            roleId={''}
            projectData={projectData?.attributes}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RepresentationAndRole;
